import { useState, useEffect } from 'react';
import { Box, BoxProps } from '../box';
import { Flex } from '../flex';
import { CaretUp, CaretDown } from '@mezzoforte/forge-icons';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  RowSelectionState,
  SortDirection,
  useReactTable,
} from '@tanstack/react-table';
import { TableProps } from './types';
import { Tr } from './tr';
import { Th } from './th';
import { Td } from './td';
import { IndeterminateCheckbox } from './indeterminate-checkbox';

type Props<T> = TableProps<T> & Omit<BoxProps, 'variant'>;

const checkboxColumnStyles = {
  minWidth: 45,
  width: 45,
  maxWidth: 45,
};

const renderSortIcon = (sort: false | SortDirection) => {
  if (!sort) return <Box style={{ width: '24px' }} />;
  return sort === 'desc' ? <CaretDown width={24} /> : <CaretUp width={24} />;
};

export const Table = <T,>({
  size = 'normal',
  customCellStyles,
  onSelections,
  width,
  maxWidth,
  columns: propsColumns,
  data,
  isSortable,
  ...props
}: Props<T>) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const columns: ColumnDef<T>[] = onSelections
    ? [
        {
          id: 'select',
          header: ({ table }) => (
            <IndeterminateCheckbox
              checked={table.getIsAllRowsSelected()}
              indeterminate={table.getIsSomeRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
            />
          ),
          cell: ({ row }) => (
            <IndeterminateCheckbox
              checked={row.getIsSelected()}
              indeterminate={row.getIsSomeSelected()}
              onChange={row.getToggleSelectedHandler()}
            />
          ),
        },
        ...propsColumns,
      ]
    : propsColumns;

  useEffect(() => {
    if (onSelections) {
      onSelections(rowSelection);
    }
  }, [rowSelection, onSelections]);

  const table = useReactTable<T>({
    columns,
    data,
    state: onSelections ? { rowSelection } : undefined,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: isSortable ? getSortedRowModel() : undefined,
    enableRowSelection: !!onSelections,
    onRowSelectionChange: onSelections ? setRowSelection : undefined,
  });

  const rows = table.getRowModel().rows;
  const headerGroups = table.getHeaderGroups();

  return (
    <Box
      width={width}
      maxWidth={maxWidth}
    >
      <Box
        as="table"
        style={{ borderSpacing: 0, tableLayout: 'fixed' }}
        {...props}
        minWidth="100%"
        maxWidth="100%"
      >
        <Box
          as="thead"
          top="0"
          left="0"
          zIndex={1}
        >
          {headerGroups.map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((column, index) => {
                return (
                  <Th
                    key={column.id}
                    index={index}
                    {...(customCellStyles
                      ? {
                          customCellStyles,
                        }
                      : {})}
                    maxWidth={
                      !!onSelections && index === 0
                        ? checkboxColumnStyles.maxWidth
                        : undefined
                    }
                    size={size}
                    onClick={
                      isSortable
                        ? column.column.getToggleSortingHandler()
                        : undefined
                    }
                  >
                    <Flex
                      alignItems="center"
                      display="inline-flex"
                      gap={1}
                    >
                      {flexRender(
                        column.column.columnDef.header,
                        column.getContext()
                      )}
                      {isSortable &&
                        renderSortIcon(column.column.getIsSorted())}
                    </Flex>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Box>

        <Box as="tbody">
          {rows.map((row) => {
            return (
              <Tr key={row.id}>
                {row.getAllCells().map((cell, index) => {
                  return (
                    <Td
                      key={cell.id}
                      index={index}
                      {...(customCellStyles
                        ? {
                            customCellStyles,
                          }
                        : {})}
                      maxWidth={
                        !!onSelections && index === 0
                          ? checkboxColumnStyles.maxWidth
                          : undefined
                      }
                      size={size}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};
